import Box from '@material-ui/core/Box'
import MUIButton from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import { DatePicker } from '@material-ui/pickers'
import { get } from 'lodash'
import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import { Button, useGetIdentity, useNotify } from 'react-admin'

import { exportTeamStatements } from '../../api/jobs'
import { captureExceptionIfProduction } from '../../helpers/utils'

const TeamMonthlyStatementsExportButton = ({ teamId }: any) => {
  const { identity, loading: identityLoading } = useGetIdentity()
  const [email, setEmail] = useState('')

  useEffect(() => {
    if (!email && !identityLoading && identity) {
      setEmail(get(identity, 'email', ''))
    }
  }, [email, identity, identityLoading])

  const [isDialogOpen, setDialogOpen] = useState(false)
  const [isSubmitLoading, setSubmitLoading] = useState(false)

  const now = DateTime.now()
  const [startDate, setStartDate] = useState(now.minus({ months: 3 })) // default to 3 months ago
  const [endDate, setEndDate] = useState(now)

  // this is a hack due to quirk of driver intelligence endpoint
  // it expects datetimes to be localized in NY timezone, and formatted as follows:
  // 1st of the month, at 0 hour, 0 min, 0 second
  const convertDateToDriverIntelligenceFormat = (date: any) =>
    DateTime.fromObject(
      {
        year: date.year,
        month: date.month,
        day: 1,
        hour: 0,
        minute: 0,
        second: 0,
      },
      {
        zone: 'America/New_York',
      }
    ).toISO()

  const notify = useNotify()

  return (
    <>
      <MUIButton
        onClick={() => setDialogOpen(true)}
        variant="contained"
        color="primary"
        disabled={identityLoading}
      >
        Team Monthly Statements Export
      </MUIButton>

      <Dialog open={isDialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Team Statements Export</DialogTitle>
        <DialogContent>
          {isSubmitLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="20vh">
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box>
                <DialogContentText>
                  We'll send the PDFs to <Box style={{ fontWeight: 'bold', display: 'inline' }}>{email}</Box>.
                </DialogContentText>
                <DialogContentText>
                  You'll receive an email from noreply@cloudtrucks.com when they are ready (this will take a
                  few minutes).
                </DialogContentText>
              </Box>
              <Box my={4}>
                <DatePicker
                  inputVariant="outlined"
                  openTo="month"
                  views={['year', 'month']}
                  label="Start Month"
                  value={startDate}
                  onChange={(date: any) => setStartDate(date)}
                  minDate={DateTime.fromISO('2020-01-01')}
                  minDateMessage="Earliest Start is January 2020"
                  maxDate={endDate}
                  maxDateMessage="Start Date must be before End Date"
                  disableFuture
                />
                <Box mt={4}>
                  <DatePicker
                    inputVariant="outlined"
                    openTo="month"
                    views={['year', 'month']}
                    label="End Month (inclusive)"
                    value={endDate}
                    onChange={(date: any) => setEndDate(date)}
                    minDate={startDate}
                    minDateMessage="End Month must be after Start Month"
                    disableFuture
                  />
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} label="Cancel" disabled={isSubmitLoading} />
          <Button
            variant="contained"
            disabled={identityLoading || !email || isSubmitLoading}
            onClick={async () => {
              try {
                setSubmitLoading(true)
                const isOK = await exportTeamStatements(
                  teamId,
                  convertDateToDriverIntelligenceFormat(startDate),
                  convertDateToDriverIntelligenceFormat(endDate.plus({ months: 1 }))
                )
                if (isOK) {
                  setDialogOpen(false)
                  setSubmitLoading(false)
                  notify('Generating your report! It may take a few minutes.', {
                    type: 'success',
                  })
                } else {
                  throw new Error('Export failed')
                }
              } catch (error) {
                setDialogOpen(false)
                setSubmitLoading(false)
                notify(`Oops, something went wrong: ${JSON.stringify(error)}`, { type: 'error' })
                captureExceptionIfProduction(error)
              }
            }}
            label="Export"
          />
        </DialogActions>
      </Dialog>
    </>
  )
}

export default TeamMonthlyStatementsExportButton
